import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Alert,
  Modal,
  Badge,
  InputGroup,
  FormControl,
  Spinner,
  Dropdown,
  Pagination,
  Form,
} from "react-bootstrap";
import {
  PersonPlus,
  Pencil,
  Trash,
  Eye,
  Envelope,
  Telephone,
  Key,
  Search,
  CheckCircle,
  XCircle,
  Send,
  Filter,
  ThreeDotsVertical,
  ArrowClockwise,
  PersonCheck,
  PersonX,
} from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import { Baseurl } from "../../baseurl";

const API_BASE_URL = "http://localhost:5000/api";

const EmployeeList = () => {
  const navigate = useNavigate();

  // State management
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    variant: "success",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Filter and pagination states
  const [filterRole, setFilterRole] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const itemsPerPage = 10;

  // Fetch employees with filters and pagination
  const fetchEmployees = async (page = 1) => {
    try {
      setLoading(true);

      const response = await axios.get(`${Baseurl}/api/employees/all`);

      setEmployees(response.data.employees || response.data.emp || []);
      setTotalEmployees(response.data.total || response.data.count || 0);
      // setTotalPages(
      //   response.data.pages ||
      //     Math.ceil((response.data.total || 0) / itemsPerPage),
      // );
      // setCurrentPage(page);
    } catch (error) {
      showAlert("Error fetching employees", "danger");
      console.error("Error fetching employees:", error);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchEmployees(1);
  }, [filterRole, filterStatus]);

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== undefined) {
        fetchEmployees(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const showAlert = (message, variant) => {
    setAlert({ show: true, message, variant });
    setTimeout(() => {
      setAlert({ show: false, message: "", variant: "success" });
    }, 5000);
  };

  // Open modal for actions
  const openModal = (type, employee = null) => {
    setModalType(type);
    setCurrentEmployee(employee);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setCurrentEmployee(null);
    setActionLoading(false);
  };

  // Handle delete employee
  const handleDelete = async () => {
    try {
      setActionLoading(true);
      await axios.delete(`${API_BASE_URL}/employees/delete`, {
        data: { _id: currentEmployee._id },
      });
      showAlert("Employee deleted successfully!", "success");
      fetchEmployees(currentPage);
      closeModal();
    } catch (error) {
      showAlert(
        error.response?.data?.message || "Error deleting employee",
        "danger",
      );
    } finally {
      setActionLoading(false);
    }
  };

  // Handle activate/deactivate
  const handleToggleStatus = async (employee) => {
    try {
      const newStatus = !employee.isActive;
      await axios.post(`${Baseurl}/api/employees/editActiveStatus`, {
        _id: employee._id,
        isActive: newStatus,
      });
      showAlert(
        `Employee ${newStatus ? "activated" : "deactivated"} successfully!`,
        "success",
      );
      fetchEmployees(currentPage);
    } catch (error) {
      showAlert(
        error.response?.data?.message || "Error updating employee status",
        "danger",
      );
    }
  };

  // Send OTP to employee
  const handleSendOtp = async () => {
    try {
      setActionLoading(true);
      await axios.post(`${API_BASE_URL}/employees/send-otp`, {
        phoneNo: currentEmployee.phoneNo,
      });
      showAlert("OTP sent successfully!", "success");
      closeModal();
    } catch (error) {
      showAlert(error.response?.data?.message || "Error sending OTP", "danger");
    } finally {
      setActionLoading(false);
    }
  };

  // Reset password (send reset OTP)
  const handleResetPassword = async () => {
    try {
      setActionLoading(true);
      await axios.post(`${API_BASE_URL}/employees/send-reset-otp`, {
        email: currentEmployee.email,
      });
      showAlert("Password reset OTP sent to employee email!", "success");
      closeModal();
    } catch (error) {
      showAlert(
        error.response?.data?.message || "Error sending reset OTP",
        "danger",
      );
    } finally {
      setActionLoading(false);
    }
  };

  // Navigate to edit page
  const handleEdit = (employee) => {
    navigate(`/employees/edit/${employee._id}`, { state: { employee } });
  };

  // Navigate to add employee page
  const handleAddEmployee = () => {
    navigate("/employees/add");
  };

  // Get role badge color
  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "agent":
        return "danger";
      case "inspector":
        return "warning";
      case "yardOwner":
        return "info";
      default:
        return "secondary";
    }
  };

  // Filter employees locally for search (fallback)
  const filteredEmployees = employees.filter(
    (emp) => emp.name?.toLowerCase().includes(searchTerm.toLowerCase()),
    // emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    // emp.phoneNo?.includes(searchTerm) ||
    // emp.role?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Pagination items
  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => fetchEmployees(page)}
        >
          {page}
        </Pagination.Item>,
      );
    }

    return items;
  };

  return (
    <Container fluid className="py-4">
      {/* Alert */}
      {alert.show && (
        <Alert
          variant={alert.variant}
          onClose={() =>
            setAlert({ show: false, message: "", variant: "success" })
          }
          dismissible
        >
          {alert.message}
        </Alert>
      )}

      <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Header className=" text-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0 text-black">
                <PersonPlus className="me-2" />
                Employee Management
              </h5>
              <div className="d-flex gap-2">
                <Button
                  variant="light"
                  size="sm"
                  onClick={() => fetchEmployees(currentPage)}
                  title="Refresh"
                >
                  <ArrowClockwise />
                </Button>
                <Button variant="light" size="sm" onClick={handleAddEmployee}>
                  <PersonPlus className="me-1" /> Add Employee
                </Button>
              </div>
            </Card.Header>

            <Card.Body>
              {/* Filters and Search */}
              <Row className="mb-4">
                <Col md={4}>
                  <InputGroup>
                    <InputGroup.Text>
                      <Search />
                    </InputGroup.Text>
                    <FormControl
                      placeholder="Search employees..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </Col>
                <Col md={4}>
                  <InputGroup>
                    <InputGroup.Text>
                      <Filter />
                    </InputGroup.Text>
                    <Form.Select
                      value={filterRole}
                      onChange={(e) => setFilterRole(e.target.value)}
                    >
                      <option value="all">All Roles</option>
                      <option value="agent">Agent</option>
                      <option value="inspector">Inspector</option>
                      <option value="yardOwner">YardOwner</option>
                    </Form.Select>
                  </InputGroup>
                </Col>
                <Col md={4}>
                  <InputGroup>
                    <InputGroup.Text>
                      <Filter />
                    </InputGroup.Text>
                    <Form.Select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </Form.Select>
                  </InputGroup>
                </Col>
              </Row>

              {/* Summary Stats */}
              <Row className="mb-4">
                <Col md={3}>
                  <Card className="border-primary">
                    <Card.Body className="py-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-0">Total Employees</h6>
                          <h4 className="mb-0 text-primary">
                            {totalEmployees}
                          </h4>
                        </div>
                        <PersonPlus size={32} className="text-primary" />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="border-success">
                    <Card.Body className="py-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-0">Active</h6>
                          <h4 className="mb-0 text-success">
                            {employees.filter((e) => e.isActive).length}
                          </h4>
                        </div>
                        <PersonCheck size={32} className="text-success" />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="border-warning">
                    <Card.Body className="py-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-0">Inspectors</h6>
                          <h4 className="mb-0 text-warning">
                            {
                              employees.filter((e) => e.role === "inspector")
                                .length
                            }
                          </h4>
                        </div>
                        <Filter size={32} className="text-warning" />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="border-info">
                    <Card.Body className="py-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-0">Admins</h6>
                          <h4 className="mb-0 text-info">
                            {employees.filter((e) => e.role === "admin").length}
                          </h4>
                        </div>
                        <Key size={32} className="text-info" />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Employees Table */}
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2">Loading employees...</p>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <Table striped bordered hover className="mb-0">
                      <thead>
                        <tr className="bg-light">
                          <th>#</th>
                          <th>Name</th>
                          <th>Contact</th>
                          <th>Role</th>
                          <th>Status</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredEmployees.length > 0 ? (
                          filteredEmployees.map((employee, index) => (
                            <tr key={employee._id}>
                              <td className="px-2">
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </td>
                              <td className="px-2">
                                <strong>{employee.name}</strong>
                                <div className="small text-muted">
                                  ID: {employee._id?.substring(0, 8)}...
                                </div>
                              </td>
                              <td className="px-2">
                                <div className="d-flex flex-column mx-2">
                                  <span>
                                    <Envelope
                                      className="me-1 text-muted"
                                      size={14}
                                    />
                                    {employee.email}
                                  </span>
                                  <span>
                                    <Telephone
                                      className="me-1 text-muted"
                                      size={14}
                                    />
                                    {employee.phoneNo}
                                  </span>
                                </div>
                              </td>
                              <td className="px-2">
                                <Badge bg={getRoleBadgeColor(employee.role)}>
                                  {employee.role?.toUpperCase()}
                                </Badge>
                              </td>
                              <td className="px-2">
                                {employee.isActive ? (
                                  <Badge
                                    bg="success"
                                    className="d-flex align-items-center gap-1"
                                    style={{ width: "fit-content" }}
                                  >
                                    <CheckCircle size={12} />
                                    Active
                                  </Badge>
                                ) : (
                                  <Badge
                                    bg="secondary"
                                    className="d-flex align-items-center gap-1"
                                    style={{ width: "fit-content" }}
                                  >
                                    <XCircle size={12} />
                                    Inactive
                                  </Badge>
                                )}
                              </td>
                              <td className="px-2">
                                {new Date(
                                  employee.createdAt,
                                ).toLocaleDateString()}
                                <div className="small text-muted">
                                  {new Date(
                                    employee.createdAt,
                                  ).toLocaleTimeString()}
                                </div>
                              </td>
                              <td>
                                <Dropdown>
                                  <Dropdown.Toggle
                                    variant="light"
                                    size="sm"
                                    id="dropdown-basic"
                                  >
                                    <ThreeDotsVertical size={16} />
                                  </Dropdown.Toggle>

                                  <Dropdown.Menu>
                                    <Dropdown.Item
                                      onClick={() =>
                                        openModal("view", employee)
                                      }
                                    >
                                      <Eye className="me-2" size={14} />
                                      View Details
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      onClick={() => handleEdit(employee)}
                                    >
                                      <Pencil className="me-2" size={14} />
                                      Edit Employee
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      onClick={() =>
                                        openModal("sendOtp", employee)
                                      }
                                    >
                                      <Send className="me-2" size={14} />
                                      Send OTP
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      onClick={() =>
                                        openModal("resetPassword", employee)
                                      }
                                    >
                                      <Key className="me-2" size={14} />
                                      Reset Password
                                    </Dropdown.Item>
                                    <Dropdown.Divider />
                                    <Dropdown.Item
                                      onClick={() =>
                                        handleToggleStatus(employee)
                                      }
                                      className={
                                        employee.isActive
                                          ? "text-danger"
                                          : "text-success"
                                      }
                                    >
                                      {employee.isActive ? (
                                        <>
                                          <PersonX className="me-2" size={14} />
                                          Deactivate
                                        </>
                                      ) : (
                                        <>
                                          <PersonCheck
                                            className="me-2"
                                            size={14}
                                          />
                                          Activate
                                        </>
                                      )}
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      onClick={() =>
                                        openModal("delete", employee)
                                      }
                                      className="text-danger"
                                    >
                                      <Trash className="me-2" size={14} />
                                      Delete
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="text-center py-5">
                              <div className="text-muted">
                                <PersonX size={48} className="mb-3" />
                                <h5>No employees found</h5>
                                <p>Try adjusting your search or filters</p>
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={handleAddEmployee}
                                >
                                  <PersonPlus className="me-1" /> Add First
                                  Employee
                                </Button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div className="text-muted">
                        Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                        {Math.min(currentPage * itemsPerPage, totalEmployees)}{" "}
                        of {totalEmployees} employees
                      </div>
                      <Pagination>
                        <Pagination.First
                          onClick={() => fetchEmployees(1)}
                          disabled={currentPage === 1}
                        />
                        <Pagination.Prev
                          onClick={() => fetchEmployees(currentPage - 1)}
                          disabled={currentPage === 1}
                        />
                        {renderPaginationItems()}
                        <Pagination.Next
                          onClick={() => fetchEmployees(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        />
                        <Pagination.Last
                          onClick={() => fetchEmployees(totalPages)}
                          disabled={currentPage === totalPages}
                        />
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modals */}
      {/* View Employee Modal */}
      <Modal
        show={showModal && modalType === "view"}
        onHide={closeModal}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <Eye className="me-2" />
            Employee Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentEmployee && (
            <Row>
              <Col md={6}>
                <h6>Personal Information</h6>
                <p>
                  <strong>Name:</strong> {currentEmployee.name}
                </p>
                <p>
                  <strong>Email:</strong> {currentEmployee.email}
                </p>
                <p>
                  <strong>Phone:</strong> {currentEmployee.phoneNo}
                </p>
                <p>
                  <strong>Role:</strong>{" "}
                  <Badge bg={getRoleBadgeColor(currentEmployee.role)}>
                    {currentEmployee.role}
                  </Badge>
                </p>
              </Col>
              <Col md={6}>
                <h6>Account Information</h6>
                <p>
                  <strong>Status:</strong>{" "}
                  {currentEmployee.isActive ? (
                    <Badge bg="success">Active</Badge>
                  ) : (
                    <Badge bg="secondary">Inactive</Badge>
                  )}
                </p>
                <p>
                  <strong>Employee ID:</strong>{" "}
                  <code>{currentEmployee._id}</code>
                </p>
                <p>
                  <strong>Created:</strong>{" "}
                  {new Date(currentEmployee.createdAt).toLocaleString()}
                </p>
                <p>
                  <strong>Last Updated:</strong>{" "}
                  {new Date(currentEmployee.updatedAt).toLocaleString()}
                </p>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Close
          </Button>
          <Button variant="primary" onClick={() => handleEdit(currentEmployee)}>
            <Pencil className="me-1" /> Edit Employee
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showModal && modalType === "delete"} onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <Trash className="me-2" />
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            <strong>Warning:</strong> This action cannot be undone.
          </Alert>
          <p>
            Are you sure you want to delete employee{" "}
            <strong>{currentEmployee?.name}</strong>?
          </p>
          <p className="text-muted small">
            This will permanently remove all associated data.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeModal}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              "Delete Employee"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Send OTP Modal */}
      <Modal show={showModal && modalType === "sendOtp"} onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            <Send className="me-2" />
            Send OTP
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Send OTP to <strong>{currentEmployee?.name}</strong>?
          </p>
          <div className="alert alert-info">
            <p className="mb-1">
              <strong>Phone:</strong> {currentEmployee?.phoneNo}
            </p>
            <p className="mb-0">
              <strong>Email:</strong> {currentEmployee?.email}
            </p>
          </div>
          <p className="text-muted small">
            OTP will be sent to the registered phone number and email.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeModal}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSendOtp}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Sending...
              </>
            ) : (
              "Send OTP"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        show={showModal && modalType === "resetPassword"}
        onHide={closeModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <Key className="me-2" />
            Reset Password
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Send password reset instructions to{" "}
            <strong>{currentEmployee?.name}</strong>?
          </p>
          <div className="alert alert-warning">
            <p className="mb-0">
              A password reset OTP will be sent to:{" "}
              <strong>{currentEmployee?.email}</strong>
            </p>
          </div>
          <p className="text-muted small">
            The employee will need to use the OTP to set a new password.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeModal}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={handleResetPassword}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Sending...
              </>
            ) : (
              "Send Reset Instructions"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default EmployeeList;
